"use client";

import { motion } from "framer-motion";
import { useLang } from "@/contexts/language-context";
import { Check, X } from "lucide-react";

type CellValue = "yes" | "no" | { en: string; es: string };

type Row = {
  label: { en: string; es: string };
  redy: CellValue;
};

// What Redy's KDS includes. No competitor columns: we only state what we can back up.
const ROWS: Row[] = [
  { label: { en: "Extra price per screen / month", es: "Precio extra por pantalla / mes" }, redy: { en: "$0", es: "$0" } },
  { label: { en: "Unlimited stations included", es: "Estaciones ilimitadas incluidas" }, redy: "yes" },
  { label: { en: "Per-station routing rules", es: "Reglas de ruteo por estación" }, redy: "yes" },
  { label: { en: "Expo screen aggregator", es: "Pantalla expo agregadora" }, redy: "yes" },
  { label: { en: "Notes and key modifiers highlighted", es: "Notas y modificadores clave resaltados" }, redy: "yes" },
  { label: { en: "Live color-coded timers", es: "Timers vivos con color" }, redy: "yes" },
  { label: { en: "Recall / void / comp flags", es: "Recall / void / comp en vivo" }, redy: "yes" },
  { label: { en: "Runs on any iPad or browser", es: "Corre en cualquier iPad o browser" }, redy: "yes" },
  { label: { en: "Kitchen times by station report", es: "Reporte de tiempos por estación" }, redy: "yes" },
];

export default function KitchenDisplayCompare() {
  const { t } = useLang();

  return (
    <section className="relative py-32 px-6 bg-white overflow-hidden">
      <div className="max-w-[1200px] mx-auto">
        <motion.div
          initial={{ opacity: 0, y: 16 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true, margin: "-100px" }}
          transition={{ duration: 0.6 }}
          className="text-center mb-16 max-w-[760px] mx-auto"
        >
          <div className="text-[11px] font-black tracking-widest uppercase mb-4" style={{ color: "#D7263D" }}>
            {t("What's in the box", "Lo que trae la caja")}
          </div>
          <h2 className="text-4xl md:text-[64px] font-black tracking-[-0.03em] leading-[1.02] text-redy-charcoal">
            {t("Every feature. ", "Cada feature. ")}
            <span className="text-redy-charcoal/30">
              {t("No add-on invoice.", "Sin factura de add-on.")}
            </span>
          </h2>
          <p className="text-[16px] font-medium text-redy-secondary leading-relaxed mt-6">
            {t(
              "Many POS systems sell the kitchen display as an add-on, billed per screen. Here's everything Redy's KDS includes — in every plan, at no extra cost.",
              "Muchos POS venden la pantalla de cocina como add-on, cobrada por pantalla. Esto es todo lo que incluye el KDS de Redy — en cada plan, sin costo extra."
            )}
          </p>
        </motion.div>

        <motion.div
          initial={{ opacity: 0, y: 24 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true, margin: "-100px" }}
          transition={{ duration: 0.7, ease: [0.21, 0.47, 0.32, 0.98] }}
          className="max-w-[920px] mx-auto"
        >
          <div className="bg-[#FAFAF8] rounded-[28px] p-3 md:p-5 border border-black/5 shadow-[0_30px_80px_rgba(0,0,0,0.04)]">
            <div className="bg-white rounded-[22px] overflow-hidden">
              {/* Header row */}
              <div className="grid grid-cols-[1.6fr_1fr] md:grid-cols-[2fr_1fr] items-stretch border-b border-black/5">
                <div className="px-4 md:px-6 py-5 flex items-end">
                  <span className="text-[10px] font-black uppercase tracking-widest text-redy-secondary/60">
                    {t("Feature", "Feature")}
                  </span>
                </div>
                <ColumnHeader label="Redy" subtitle={t("In the box", "En la caja")} highlighted />
              </div>

              {/* Body rows */}
              {ROWS.map((row, i) => (
                <motion.div
                  key={row.label.en}
                  initial={{ opacity: 0, y: 8 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true, margin: "-80px" }}
                  transition={{ duration: 0.4, delay: 0.03 * i }}
                  className="grid grid-cols-[1.6fr_1fr] md:grid-cols-[2fr_1fr] items-stretch border-b border-black/5 last:border-b-0"
                >
                  <div className="px-4 md:px-6 py-4 md:py-5 flex items-center">
                    <span className="text-[13px] md:text-[14px] font-bold text-redy-charcoal leading-snug">
                      {t(row.label.en, row.label.es)}
                    </span>
                  </div>
                  <Cell value={row.redy} highlighted />
                </motion.div>
              ))}
            </div>
          </div>

          {/* Stat banner */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true, margin: "-100px" }}
            transition={{ duration: 0.6, delay: 0.3 }}
            className="mt-8 bg-gradient-to-br from-[#F4722B] to-[#D7263D] rounded-[24px] px-8 py-7 text-white shadow-lg shadow-[#F4722B]/20"
          >
            <div className="flex flex-col md:flex-row items-start md:items-center justify-between gap-4">
              <div>
                <div className="text-[11px] font-black uppercase tracking-widest text-white/70 mb-2">
                  {t("On 4 stations, 12 months", "En 4 estaciones, 12 meses")}
                </div>
                <div className="text-[36px] md:text-[48px] font-black leading-none tabular-nums">
                  {t("$0 extra for screens.", "$0 extra por pantallas.")}
                </div>
              </div>
              <div className="text-right">
                <div className="text-[11px] font-black uppercase tracking-widest text-white/70 mb-1">
                  {t("Stations", "Estaciones")}
                </div>
                <div className="text-[18px] font-black tabular-nums text-white/70">{t("Unlimited", "Ilimitadas")}</div>
              </div>
            </div>
          </motion.div>
        </motion.div>

        <motion.p
          initial={{ opacity: 0 }}
          whileInView={{ opacity: 1 }}
          viewport={{ once: true, margin: "-100px" }}
          transition={{ duration: 0.6, delay: 0.4 }}
          className="text-center text-[12px] font-medium text-redy-secondary/60 mt-8 max-w-[640px] mx-auto"
        >
          {t(
            "The KDS is included in every Redy plan. When you compare POS quotes, ask for the kitchen-display line items.",
            "El KDS viene incluido en cada plan de Redy. Cuando compares cotizaciones de POS, pide los line items de la pantalla de cocina."
          )}
        </motion.p>
      </div>
    </section>
  );
}

function ColumnHeader({
  label,
  subtitle,
  highlighted = false,
}: {
  label: string;
  subtitle: string;
  highlighted?: boolean;
}) {
  return (
    <div
      className={`px-3 md:px-4 py-5 text-center ${highlighted ? "relative" : ""}`}
      style={highlighted ? { background: "linear-gradient(180deg, rgba(244,114,43,0.10) 0%, rgba(244,114,43,0) 100%)" } : undefined}
    >
      <div
        className={`text-[14px] md:text-[16px] font-black ${highlighted ? "" : "text-redy-charcoal/70"}`}
        style={highlighted ? { color: "#D7263D" } : undefined}
      >
        {label}
      </div>
      <div className={`text-[9px] md:text-[10px] font-bold uppercase tracking-widest mt-1 ${highlighted ? "" : "text-redy-secondary/50"}`}
        style={highlighted ? { color: "#D7263D", opacity: 0.7 } : undefined}
      >
        {subtitle}
      </div>
    </div>
  );
}

function Cell({ value, highlighted = false }: { value: CellValue; highlighted?: boolean }) {
  const { t } = useLang();
  const accent = highlighted ? "#D7263D" : undefined;

  return (
    <div className="px-3 md:px-4 py-4 md:py-5 flex items-center justify-center text-center"
      style={highlighted ? { background: "linear-gradient(180deg, rgba(244,114,43,0.04) 0%, transparent 100%)" } : undefined}
    >
      {value === "yes" ? (
        <div
          className="w-7 h-7 rounded-full flex items-center justify-center"
          style={{
            backgroundColor: highlighted ? "rgba(244, 114, 43, 0.15)" : "rgba(46, 204, 113, 0.14)",
          }}
        >
          <Check className="w-4 h-4" strokeWidth={3.5} style={{ color: highlighted ? "#D7263D" : "#2ECC71" }} />
        </div>
      ) : value === "no" ? (
        <div className="w-7 h-7 rounded-full flex items-center justify-center bg-redy-charcoal/5">
          <X className="w-4 h-4" strokeWidth={3} style={{ color: "rgba(26, 26, 26, 0.35)" }} />
        </div>
      ) : (
        <span
          className="text-[14px] md:text-[16px] font-black tabular-nums"
          style={{ color: accent ?? "rgba(26, 26, 26, 0.65)" }}
        >
          {t(value.en, value.es)}
        </span>
      )}
    </div>
  );
}
